// Plain-language guides for the hub's model types, keyed by inference_config.task.
// They give first-time visitors sample files to try, steps to test, what the model knows,
// how to read the result, and the sources to credit. Sample files live in public/demo-samples.

const EVERYAYAH = { name: 'EveryAyah.com', url: 'https://everyayah.com', note: 'verse-by-verse recitations (sample and training audio)' };
const TANZIL = { name: 'Tanzil Project', url: 'https://tanzil.net', note: 'Quran text (simple-clean)' };
const TARTEEL_WHISPER = {
  name: 'Tarteel AI whisper-tiny-ar-quran',
  url: 'https://huggingface.co/tarteel-ai/whisper-tiny-ar-quran',
  note: 'Quran-finetuned speech recognition model, Apache-2.0',
};

const DINOV2 = {
  name: 'Meta DINOv2 (facebook/dinov2-small)',
  url: 'https://huggingface.co/facebook/dinov2-small',
  note: 'pretrained image model that turns each picture into features, Apache-2.0',
};
const BISMILLAH_COLLECTORS = {
  name: 'Nur Aisya Zulkifli and Nurul Izzani Ahmad Shayuri',
  url: 'https://quranichub.fsktm.um.edu.my',
  note: 'collected the Bismillah images (Universiti Malaya)',
};

export const modelGuides = {
  'image-embedding-classification': {
    steps: [
      'Choose a sample and press "Use this sample", or upload a cropped picture of one written Bismillah.',
      'The hub turns the picture into image features and the model compares them with the expert-labelled examples it learnt from.',
      'Read the predicted label (Uthmani script or not). Compare the sample result with its recorded label.',
    ],
    knows: [
      'Written Bismillah (بسم الله الرحمن الرحيم) in many calligraphy styles, collected from photos, scans and screenshots.',
      'The current candidate uses 109 labelled development images: 36 Uthmani and 73 Not Uthmani.',
      'The original annotation sheet credits Dr Ali and PM Dr Sedek. Reviewer names for the nine added training labels are not recorded.',
    ],
    limits: [
      'This is an early research prototype. It does not certify Quranic writing correctness or tajweed.',
      'It only knows the Bismillah; other verses or other text give meaningless answers.',
      'Very small, blurred or heavily decorated images may be misread, even when the displayed model score is high.',
      'Three additional images remain reserved and unscored. A broader independent test set containing both classes is still needed.',
    ],
    resultHelp: 'These are uncalibrated classifier scores. They add up to 100%, but do not give the probability that the answer is correct. An expert should check the result.',
    samples: [
      { file: 'bismillah_uthmani_example.jpg', label: 'Uthmani example', expected: 'Recorded label: Uthmani script', note: 'Training example, supplied to demonstrate operation.' },
      { file: 'bismillah_not_uthmani_example.jpg', label: 'Not Uthmani example', expected: 'Recorded label: Not Uthmani script', note: 'Training example, supplied to demonstrate operation.' },
      { file: 'bismillah_known_error.png', label: 'A known model error', expected: 'Recorded label: Uthmani script. This candidate predicts Not Uthmani.', note: 'Training example showing why expert review is still needed.' },
    ],
    sampleSource: 'Sample images collected for the Quranic AI Data Hub, for testing only.',
    packageFile: 'bismillah-uthmani-script-image-package.zip',
    packageNote: 'The .skops model file plus this code package runs the model on your own computer (feature extraction, training and prediction, with a step-by-step README).',
    credits: [BISMILLAH_COLLECTORS, DINOV2],
  },


  'whisper-embedding-classification': {
    steps: [
      'Pick a sample below and press "Use this sample", or upload your own clip of one reciter.',
      'The hub turns the first 30 seconds into voice features and the model compares them with the six reciters it knows.',
      'Read the predicted reciter and the confidence for each reciter.',
    ],
    knows: [
      'Six reciters: Abdul Basit (Murattal), Abdullah Basfar, Abdurrahman As-Sudais, Abu Bakr Ash-Shaatree, Mahmoud Khalil Al-Husary, Muhammad Siddiq Al-Minshawy (Murattal).',
      'Trained on Surahs 36, 18, 55 and 78 (1,866 verse clips).',
      'Tested on Surahs 1, 67, 112, 113 and 114, which it never heard in training: 99.4% correct on 312 clips.',
    ],
    limits: [
      'Any other reciter is matched to the closest of the six, often with high confidence.',
      'All clips come from EveryAyah studio recordings; phone or live recordings may score lower.',
    ],
    resultHelp: 'Confidence is the model\'s probability for each reciter (all six add up to 100%). It is reliable only for the six reciters listed above.',
    samples: [
      { file: 'reciter_husary_114-1.mp3', label: 'Husary, Surah An-Nas 114:1', expected: 'Mahmoud Khalil Al-Husary', note: 'A surah the model never trained on.' },
      { file: 'reciter_sudais_67-1.mp3', label: 'Sudais, Surah Al-Mulk 67:1', expected: 'Abdurrahman As-Sudais', note: 'A surah the model never trained on.' },
      { file: 'reciter_abdulbasit_113-1.mp3', label: 'Abdul Basit, Surah Al-Falaq 113:1', expected: 'Abdul Basit Abdus Samad (Murattal)', note: 'A surah the model never trained on.' },
    ],
    packageFile: 'quran-reciter-identification-code.zip',
    packageNote: 'The .skops model file plus this code package runs the model on your own computer (feature extraction and prediction, with a step-by-step README).',
    credits: [EVERYAYAH, TARTEEL_WHISPER],
  },

  'quran-ayah-identification': {
    steps: [
      'Pick a sample below and press "Use this sample", or upload a recitation of one ayah (or a few).',
      'The hub writes down the Arabic it hears, then searches all 6,236 ayahs of the Quran for the closest text.',
      'Read the most likely Surah and Ayah, what was heard, and the five closest matches.',
    ],
    knows: [
      'The whole Quran: any surah, any reciter.',
      'Tested on 522 EveryAyah clips (6 reciters, Surahs 36 and 112): 97.7% exact first match, 100% when ayahs with identical text are counted.',
    ],
    limits: [
      'Some ayahs share exactly the same words (e.g. 55:13 appears 31 times); the page lists the other places when this happens.',
      'Very short clips or heavy background noise reduce accuracy.',
      'The test clips may overlap with the speech model\'s own training data, so real-world accuracy can be lower.',
    ],
    resultHelp: 'Text match is how closely the heard words match each ayah (100% = identical text). It is not a probability.',
    samples: [
      { file: 'ayah_shaatree_36-70.mp3', label: 'Shaatree reciting one ayah of Ya-Sin', expected: 'Surah 36, Ayah 70' },
      { file: 'ayah_minshawy_112-2.mp3', label: 'Minshawy, a short ayah', expected: 'Surah 112, Ayah 2' },
      { file: 'ayah_basfar_67-2.mp3', label: 'Basfar, a longer ayah', expected: 'Surah 67, Ayah 2' },
    ],
    packageFile: 'quran-ayah-identification-package.zip',
    packageNote: 'This model runs on the hub\'s server. The package has the code, settings and a README to run it on your own computer; the speech model weights download with one command.',
    credits: [TARTEEL_WHISPER, TANZIL, EVERYAYAH],
  },

  'quran-recitation-check': {
    steps: [
      'Pick a sample below and press "Use this sample", or upload a recording of Bismillahir Rahmanir Rahim.',
      'The hub writes down the Arabic it hears and lines it up word by word with the correct text.',
      'Each word turns green (correct), red (a different word was heard) or grey (not heard).',
    ],
    knows: [
      'The four words of the Bismillah (Al-Fatihah 1:1).',
      'Tested on the Bismillah by 6 reciters: 5 marked correct; Sudais was wrongly flagged because the speech model misheard that recording.',
    ],
    limits: [
      'It checks which words were said, not tajweed, makharij or fine pronunciation; diacritics are ignored.',
      'A red word means "please listen again", not a confirmed mistake.',
    ],
    resultHelp: 'Words correct is the share of the four words that were heard correctly.',
    samples: [
      { file: 'bismillah_husary_correct.mp3', label: 'Husary, full Bismillah', expected: 'Correct: all four words green' },
      { file: 'bismillah_husary_last_word_missing.mp3', label: 'The same recording cut in half', expected: 'Mistakes found: the last word (الرحيم) grey' },
      { file: 'bismillah_test_different_ayah_112-1.mp3', label: 'A different ayah (Al-Ikhlas 112:1)', expected: 'Does not match the expected ayah' },
    ],
    packageFile: 'bismillah-recitation-checker-package.zip',
    packageNote: 'This checker runs on the hub\'s server. The package has the code and a README to run it on your own computer, for the Bismillah or any other ayah.',
    credits: [TARTEEL_WHISPER, TANZIL, EVERYAYAH],
  },
};

export const getModelGuide = (model) => modelGuides[(model?.inference_config || {}).task] || null;

// "balanced_accuracy_unseen_surahs" -> "Balanced accuracy unseen surahs"
export const readableMetricName = (key) => {
  const words = String(key).replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};
